import { api, ApiError } from "./api.js";
import { esc, toast } from "./utils.js";
import { confirmAction } from "./confirmDialog.js";

/**
 * The external-links panel shared by dive sites and cohorts.
 *
 * Both owners render the same list; they differ only in who may add and
 * who may remove, which the caller supplies. Everything else — the
 * markup, the add form, the confirm-before-remove — is identical, so it
 * lives here rather than being written twice.
 */

/** Only ever put these in an href.
 *
 * The API validates on the way in, but a value that predates that check,
 * or arrives from anywhere else, must not become script just because it
 * reached the browser. Anything else renders as inert text. */
function safeHref(url) {
  const scheme = String(url || "").slice(0, 16).toLowerCase();
  return scheme.startsWith("https://") || scheme.startsWith("http://") ? url : null;
}

/** Falls back to the host, so a link is never a bare unexplained string. */
function linkText(link) {
  if (link.label) return link.label;
  try {
    return new URL(link.url).host.replace(/^www\./, "");
  } catch (_) {
    return link.url;
  }
}

function linkRowHtml(link, canManage) {
  const href = safeHref(link.url);
  const text = linkText(link);
  const body = href
    ? `<a href="${esc(href)}" target="_blank" rel="noopener noreferrer nofollow"
          class="text-decoration-none">${esc(text)}<i class="bi bi-box-arrow-up-right ms-1 small"></i></a>`
    : `<span class="text-slate">${esc(text)}</span>`;

  // The edit form ships with the row and stays hidden until asked for.
  // Editing in place keeps the link's position in the list, which
  // remove-and-re-add does not — a correction should not send a link to
  // the bottom.
  const editForm = `
    <form class="link-edit-form d-none w-100">
      <div class="mb-2">
        <label class="form-label small mb-1" for="link-url-${link.id}">Web address</label>
        <input type="text" class="form-control form-control-sm link-edit-url"
               id="link-url-${link.id}" value="${esc(link.url)}" required />
      </div>
      <div class="mb-2">
        <label class="form-label small mb-1" for="link-label-${link.id}">Label (optional)</label>
        <input type="text" class="form-control form-control-sm link-edit-label"
               id="link-label-${link.id}" maxlength="120" value="${esc(link.label || "")}" />
      </div>
      <div class="link-edit-error text-danger small mb-2 d-none"></div>
      <div class="d-flex gap-2">
        <button type="submit" class="btn btn-sm btn-primary link-edit-save">Save</button>
        <button type="button" class="btn btn-sm btn-outline-secondary link-edit-cancel">Cancel</button>
      </div>
    </form>`;

  return `
    <li class="py-1" data-link-id="${link.id}">
      <div class="d-flex align-items-center gap-2 link-display">
        <i class="bi bi-link-45deg text-slate"></i>
        <div class="flex-grow-1 text-truncate">
          ${body}
          ${link.label ? `<div class="text-slate small text-truncate">${esc(link.url)}</div>` : ""}
        </div>
        ${canManage
          ? `<button type="button" class="btn btn-sm btn-link text-slate p-0 link-edit"
                     aria-label="Edit this link"><i class="bi bi-pencil"></i></button>
             <button type="button" class="btn btn-sm btn-link text-danger p-0 link-remove"
                     aria-label="Remove this link"><i class="bi bi-x-lg"></i></button>`
          : ""}
      </div>
      ${canManage ? editForm : ""}
    </li>`;
}

/**
 * `canManage` is a predicate rather than a flag: on a dive site anyone
 * may add a link but only its author (or a moderator) may edit or
 * remove it, so the controls are decided per row.
 */
export function linksSectionHtml(links, { canAdd = false, canManage = () => false } = {}) {
  const list = links || [];
  // Nothing to show and nothing to do: render no panel at all rather
  // than an empty box on every dive site.
  if (!list.length && !canAdd) return "";

  return `
    <div class="card mb-4" id="links-section">
      <div class="card-body">
        <div class="d-flex justify-content-between align-items-center mb-2">
          <h2 class="h6 mb-0"><i class="bi bi-link-45deg me-1"></i>Links</h2>
          ${canAdd
            ? `<button type="button" class="btn btn-sm btn-outline-primary" id="link-add-btn">
                 <i class="bi bi-plus-lg me-1"></i>Add
               </button>`
            : ""}
        </div>
        <ul class="list-unstyled mb-0" id="links-list">
          ${list.length
            ? list.map((link) => linkRowHtml(link, canManage(link))).join("")
            : `<li class="text-slate small">No links yet.</li>`}
        </ul>
        ${canAdd
          ? `<form id="link-add-form" class="mt-3 d-none">
               <div class="mb-2">
                 <label class="form-label small mb-1" for="link-url">Web address</label>
                 <input type="text" class="form-control form-control-sm" id="link-url"
                        placeholder="example.com/tides" required />
               </div>
               <div class="mb-2">
                 <label class="form-label small mb-1" for="link-label">Label (optional)</label>
                 <input type="text" class="form-control form-control-sm" id="link-label"
                        maxlength="120" placeholder="Tide chart" />
               </div>
               <div id="link-error" class="text-danger small mb-2 d-none"></div>
               <div class="d-flex gap-2">
                 <button type="submit" class="btn btn-sm btn-primary" id="link-save">Add link</button>
                 <button type="button" class="btn btn-sm btn-outline-secondary" id="link-cancel">Cancel</button>
               </div>
             </form>`
          : ""}
      </div>
    </div>`;
}

/**
 * Wires the panel up.
 *
 * `basePath` is the collection endpoint ("/dive-sites/7/links"); `reload`
 * is called after a successful change so the caller can refetch and
 * repaint on its own terms.
 */
export function attachLinkHandlers(container, { basePath, reload }) {
  const section = container.querySelector("#links-section");
  if (!section) return;

  const form = section.querySelector("#link-add-form");
  const addBtn = section.querySelector("#link-add-btn");

  if (addBtn && form) {
    addBtn.addEventListener("click", () => {
      form.classList.toggle("d-none");
      if (!form.classList.contains("d-none")) section.querySelector("#link-url").focus();
    });
    section.querySelector("#link-cancel").addEventListener("click", () => {
      form.classList.add("d-none");
      form.reset();
    });

    form.addEventListener("submit", async (e) => {
      e.preventDefault();
      const errorEl = section.querySelector("#link-error");
      const saveBtn = section.querySelector("#link-save");
      errorEl.classList.add("d-none");
      const url = section.querySelector("#link-url").value.trim();
      if (!url) return;

      saveBtn.disabled = true;
      try {
        await api.post(basePath, {
          url,
          label: section.querySelector("#link-label").value.trim() || null,
        });
        form.reset();
        form.classList.add("d-none");
        await reload();
      } catch (err) {
        // Inline rather than a toast: the message is about the field
        // they are looking at, and they need it while they fix it.
        errorEl.textContent =
          err instanceof ApiError ? err.message : "Could not add that link.";
        errorEl.classList.remove("d-none");
      } finally {
        saveBtn.disabled = false;
      }
    });
  }

  section.querySelectorAll("[data-link-id]").forEach((row) => {
    const editBtn = row.querySelector(".link-edit");
    const editForm = row.querySelector(".link-edit-form");
    if (!editBtn || !editForm) return;

    const display = row.querySelector(".link-display");
    const errorEl = row.querySelector(".link-edit-error");
    // Captured now, so Cancel restores what was there when the row was
    // drawn rather than whatever a half-finished edit left behind.
    const originalUrl = row.querySelector(".link-edit-url").value;
    const originalLabel = row.querySelector(".link-edit-label").value;

    function close() {
      editForm.classList.add("d-none");
      display.classList.remove("d-none");
      errorEl.classList.add("d-none");
    }

    editBtn.addEventListener("click", () => {
      display.classList.add("d-none");
      editForm.classList.remove("d-none");
      row.querySelector(".link-edit-url").focus();
    });

    row.querySelector(".link-edit-cancel").addEventListener("click", () => {
      row.querySelector(".link-edit-url").value = originalUrl;
      row.querySelector(".link-edit-label").value = originalLabel;
      close();
    });

    editForm.addEventListener("submit", async (e) => {
      e.preventDefault();
      errorEl.classList.add("d-none");
      const saveBtn = row.querySelector(".link-edit-save");
      const url = row.querySelector(".link-edit-url").value.trim();
      if (!url) return;

      saveBtn.disabled = true;
      try {
        await api.patch(`${basePath}/${row.dataset.linkId}`, {
          url,
          label: row.querySelector(".link-edit-label").value.trim() || null,
        });
        close();
        await reload();
      } catch (err) {
        // Inline, beside the field being corrected — a toast would be
        // gone by the time they look back at the input.
        errorEl.textContent =
          err instanceof ApiError ? err.message : "Could not save that link.";
        errorEl.classList.remove("d-none");
      } finally {
        saveBtn.disabled = false;
      }
    });
  });

  section.querySelectorAll(".link-remove").forEach((btn) => {
    btn.addEventListener("click", async () => {
      const row = btn.closest("[data-link-id]");
      const confirmed = await confirmAction({
        title: "Remove this link?",
        body: "It will no longer appear here.",
        detail: row.querySelector("a, span")?.textContent?.trim() || "",
        confirmLabel: "Remove",
      });
      if (!confirmed) return;
      try {
        await api.delete(`${basePath}/${row.dataset.linkId}`);
        await reload();
      } catch (err) {
        toast(err instanceof ApiError ? err.message : "Could not remove that link.", "danger");
      }
    });
  });
}
